import { EventEmitter, Injectable } from "@angular/core";
import jwt_decode from "jwt-decode";
import { ApiPortalService } from "./api.portal.service";
import {} from "./model/auth.model";
import * as admin from "./model/user.model";
@Injectable({
  providedIn: "root",
})
export class LoginService {
  public static adminRole = "admin";
  public loggedIn: boolean = false;
  public loggedUser: admin.User = null;

  public loginChanged: EventEmitter<admin.User> = new EventEmitter();

  private decodedToken: any = null;
  private tokenExpirationTimer: any = null;
  private readonly EXPIRATION_BUFFER = 5000; // 5 seconds before actual expiration

  constructor(private api: ApiPortalService) {
    this.tryLoadUser();
  }

  private setupExpirationTimer(expirationTime: number) {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }

    const timeUntilExpiry = expirationTime * 1000 - Date.now() - this.EXPIRATION_BUFFER;
    
    if (timeUntilExpiry > 0) {
      this.tokenExpirationTimer = setTimeout(() => {
        console.log('Token is about to expire. Logging out...');
        this.logout();
      }, timeUntilExpiry);
    } else {
      // Token is already expired or about to expire
      this.logout();
    }
  }

  public get user(): admin.User {
    return this.loggedUser;
  }

  /**
   * User identifier resolved from JWT claims.
   * Claim naming differs between environments; this getter is defensive.
   */
  public get userId(): number | null {
    const decoded = this.decodedToken ?? this.tryDecodeTokenFromStorage();
    return this.extractUserId(decoded);
  }

  public tryLoadUser() {
    const token = localStorage.getItem("authToken");
    if (token && token !== "null") {
      this.setAuthToken(token);
    } else {
      this.loggedIn = false;
    }
  }

  public hasRole(role: string): boolean {
    return this.roles().includes(role);
  }


  public roles(): Array<string> {
    return this.normalizeRoles(this.decodedToken?.["roles"]);
  }

  public get hasAnyAdminRole(): boolean {
    return this.roles().some((r: string) => r.startsWith("admin"));
  }

  public get isAdmin(): boolean {
    return this.hasRole(LoginService.adminRole);
  }

  public get authToken(): string {
    return localStorage.getItem("authToken");
  }

  public setAuthToken(token: string): void {
    const decodedToken = this.decodeJwtTokenOrNull(token);
    if (!decodedToken || typeof decodedToken !== "object" || Array.isArray(decodedToken)) {
      this.logout();
      return;
    }

    this.decodedToken = decodedToken;
    localStorage.setItem("authToken", token);

    // Set authToken to cookie
    this.setAuthTokenCookie(token, this.decodedToken["exp"]);

    // Get expiration time from token (exp claim is in seconds)
    const expirationTime = this.decodedToken["exp"];
    if (expirationTime) {
      this.setupExpirationTimer(expirationTime);
    }

    const user: admin.User = {
      Login: this.decodedToken["sub"],
      SelectedBusinessUnit: this.parseNumber(this.decodedToken["selectedBusinessUnit"]),
      BusinessUnitName: this.decodedToken["businessUnitName"],
      DisplayName: this.decodedToken["name"],
      CurrencyCode: this.decodedToken["currencyCode"],
      Email: this.decodedToken["email"],
      Roles: this.normalizeRoles(this.decodedToken["roles"]),
    } as admin.User;

    this.loggedUser = user;
    this.loggedIn = true;
    localStorage.setItem("user", JSON.stringify(user));
    this.loginChanged.emit(user);
  }

  /**
   * Set authToken cookie with expiration matching the JWT token expiration
   * Cookie is set with appropriate Domain attribute to enable cross-subdomain sharing:
   * - For localhost/IP addresses: omit Domain (port-agnostic, same-host only)
   * - For subdomains (e.g., test-pos-ng.dertouristik.cz): use parent domain (.dertouristik.cz)
   * @param token The JWT token to store in cookie
   * @param expirationTime Token expiration time in seconds (Unix timestamp)
   */
  private setAuthTokenCookie(token: string, expirationTime?: number): void {
    const hostname = window.location.hostname;
    let cookieString = `authToken=${encodeURIComponent(token)}; path=/; SameSite=Strict`;
    const cookieDomain = this.getCookieDomain(hostname);

    if (cookieDomain) {
      cookieString += `; Domain=${cookieDomain}`;
    }

    if (expirationTime) {
      // expirationTime is in seconds, convert to milliseconds
      const expirationDate = new Date(expirationTime * 1000);
      cookieString += `; expires=${expirationDate.toUTCString()}`;
    }

    document.cookie = cookieString;
    console.log('[LoginService] Set authToken cookie with Domain:', cookieDomain ?? 'omitted (localhost/IP)');
  }

  /**
   * Remove authToken cookie
   */
  private removeAuthTokenCookie(): void {
    const cookieExpiration = "expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = `authToken=; ${cookieExpiration}`;

    const cookieDomain = this.getCookieDomain(window.location.hostname);
    if (cookieDomain) {
      document.cookie = `authToken=; ${cookieExpiration} Domain=${cookieDomain};`;
    }
  }

  private getCookieDomain(hostname: string): string | null {
    // For localhost and IP addresses, omit Domain so the cookie is host-only.
    if (hostname === 'localhost' || /^[\d.]+$/.test(hostname)) {
      return null;
    }

    // Share the cookie across subdomains, e.g. test-pos-ng.dertouristik.cz -> .dertouristik.cz.
    const domainParts = hostname.split('.');
    return domainParts.length >= 2
      ? `.${domainParts.slice(-2).join('.')}`
      : hostname;
  }

  private isValidJwtToken(token: string): boolean {
    if (typeof token !== "string") {
      return false;
    }

    const tokenParts = token.split(".");
    return tokenParts.length === 3
      && tokenParts.every(part => /^[A-Za-z0-9_-]+$/.test(part) && part.length % 4 !== 1);
  }

  private decodeJwtTokenOrNull(token: string | null): any | null {
    if (!this.isValidJwtToken(token)) {
      return null;
    }

    // jwt_decode can still throw for malformed JSON payloads after the structural validation above.
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  }

  private tryDecodeTokenFromStorage(): any | null {
    const token = localStorage.getItem("authToken");
    return this.decodeJwtTokenOrNull(token);
  }

  private extractUserId(decodedToken: any): number | null {
    if (!decodedToken) {
      return null;
    }
    return decodedToken?.id;
  }

  private parseNumber(value: any): number | null {
    if (value == null) {
      return null;
    }
    const parsed = typeof value === "string" ? parseInt(value, 10) : value;
    return Number.isFinite(parsed) ? (parsed as number) : null;
  }

  private normalizeRoles(rawRoles: any): Array<string> {
    let roles: Array<string>;

    if (Array.isArray(rawRoles)) {
      roles = rawRoles;
    } else if (typeof rawRoles === "string") {
      // token may provide single role ("seller") or delimited list
      roles = rawRoles.split(/[,\s]+/);
    } else {
      roles = [];
    }

    return [...new Set(
      roles
        .filter(role => typeof role === "string")
        .map(role => role.trim().toLowerCase())
        .filter(Boolean),
    )];
  }

  public logout(): void {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }

    localStorage.removeItem("authToken");
    this.removeAuthTokenCookie();
    this.decodedToken = null;
    this.loggedUser = null;
    this.loggedIn = false;
    this.loginChanged.emit(null);
  }
}
